import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Kind } from "@norkron/web-review-workbench";
import {
  articleApi,
  type ResourceRole,
  type Section,
  type SectionLink,
  type SectionVersion,
} from "../../api/articleUnits";
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  MessagesSquare,
  Repeat2,
  RotateCcw,
  X,
} from "../../compose/Icon";
import { formatDate } from "../../lib/format";
import { roleTitle } from "../../lib/workbenchLayout";

/**
 * `<SectionHandlePopover>` — the single section-scoped menu the margin grip opens
 * (design §3). It re-homes 100% of the two killed chip rows' actions onto the
 * EXISTING handlers and adds the version affordances the chips never had:
 *
 *   · header  — `§N · heading · v{n}` + the head change line (kind · editor · date)
 *   · compare — the section-scoped `<CompareTray>`/`<SideBySideDiff>` pipeline
 *               (`section-compare-{kind}-{i}` test-id kept, so the compare-flow test
 *               survives with only a reveal step prepended)
 *   · history — the section's `el.section_versions` stream (reuses the `ev-version`
 *               row visual)
 *   · link    — ARTICLE pane only: staleness "refers to Brief v1 (now v2)" + the ↔
 *               counterpart scroll + Re-pin (re-homed from `SectionLinkChips`)
 *   · comment — a section-scoped comment (integrates the just-landed comment flow)
 *
 * Portaled to `<body>` (like `<SideBySideDiff>`/`<CompareTray>`) so it adopts the
 * app-theme chrome; dismiss on Esc / outside-click / scroll-away.
 */

export interface SectionHandlePopoverProps {
  readonly section: Section;
  readonly kind: Kind;
  readonly index: number;
  /** The grip's viewport rect — the popover anchors to its right (flips left at
   *  the viewport edge). */
  readonly anchorRect: DOMRect;
  /** The cross-resource link for this section (article pane only), or null. */
  readonly link: SectionLink | null;
  /** Fallback head seq when the stream is not yet hydrated (design §2.4). */
  readonly fallbackSeq: number;
  readonly onCompare: () => void;
  readonly onRepin: (linkId: number) => void;
  readonly onCounterpart: (role: ResourceRole) => void;
  readonly onComment: () => void;
  readonly onClose: () => void;
}

const POP_WIDTH = 300;

export function SectionHandlePopover({
  section,
  kind,
  index,
  anchorRect,
  link,
  fallbackSeq,
  onCompare,
  onRepin,
  onCounterpart,
  onComment,
  onClose,
}: SectionHandlePopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [versions, setVersions] = useState<SectionVersion[]>(
    section.versions.length > 0 ? section.versions : [],
  );
  const [historyOpen, setHistoryOpen] = useState(false);

  // Hydrate the section's version stream once (header change line + history list).
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const vs = await articleApi.sectionVersions(section.id);
        if (live && vs.length > 0) setVersions(vs);
      } catch {
        // Header still shows the fallback seq; history renders what we have.
      }
    })();
    return () => {
      live = false;
    };
  }, [section.id]);

  // Dismiss on Esc / outside pointer / scroll-away (matches the compare tray).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onScroll = () => onClose();
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown, true);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  const head = versions.length > 0 ? versions[versions.length - 1] : null;
  const headSeq = head?.seq ?? fallbackSeq;
  const stale = link != null && (link.stale_other || link.stale_article);

  const runCompare = useCallback(() => {
    onCompare();
    onClose();
  }, [onCompare, onClose]);

  // Flip left when the right-anchored popover would overflow the viewport.
  const spaceRight =
    typeof window !== "undefined" ? window.innerWidth : anchorRect.right + POP_WIDTH + 40;
  const flipLeft = anchorRect.right + POP_WIDTH + 16 > spaceRight;
  const left = flipLeft
    ? Math.max(8, anchorRect.left - POP_WIDTH - 8)
    : anchorRect.right + 8;
  const top = Math.max(8, anchorRect.top);

  return createPortal(
    <div
      ref={ref}
      className="wb-shandle-pop"
      role="menu"
      aria-label={`Section ${section.ord + 1} actions`}
      data-testid={`section-popover-${kind}-${index}`}
      style={{ left, top, width: POP_WIDTH }}
    >
      <div className="wb-shandle-pop__head">
        <span className="wb-shandle-pop__title" title={section.heading}>
          §{section.ord + 1} {section.heading || "Lead"}
        </span>
        <span
          className="wb-shandle-pop__ver"
          data-testid={`section-handle-ver-${kind}-${index}`}
          title={section.content_hash}
        >
          v{headSeq}
        </span>
        <button
          type="button"
          className="wb-shandle-pop__close"
          data-testid={`section-popover-close-${kind}-${index}`}
          aria-label="Close section menu"
          onClick={onClose}
        >
          <X size={13} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
      {head ? (
        <div className="wb-shandle-pop__sub">
          {head.change_kind} in v{head.seq}
          {head.editor_name ? ` · ${head.editor_name}` : ""} ·{" "}
          {formatDate(head.created_at)}
        </div>
      ) : null}

      <div className="wb-shandle-pop__group" role="none">
        <button
          type="button"
          role="menuitem"
          className="wb-shandle-pop__item"
          data-testid={`section-compare-${kind}-${index}`}
          onClick={runCompare}
        >
          <Repeat2 size={14} strokeWidth={1.5} aria-hidden="true" />
          Compare versions…
        </button>
        <button
          type="button"
          role="menuitem"
          className="wb-shandle-pop__item"
          data-testid={`section-history-${kind}-${index}`}
          aria-expanded={historyOpen}
          onClick={() => setHistoryOpen((v) => !v)}
        >
          <Clock size={14} strokeWidth={1.5} aria-hidden="true" />
          Version history
          <span className="wb-shandle-pop__count">{versions.length}</span>
        </button>
        {historyOpen && versions.length > 0 ? (
          <ul className="wb-shandle-pop__history" aria-label="Section versions">
            {[...versions]
              .sort((a, b) => b.seq - a.seq)
              .map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    role="menuitem"
                    className="ev-version wb-shandle-pop__vrow"
                    data-testid={`section-version-row-${kind}-${index}-${v.seq}`}
                    aria-label={`Version ${v.seq}, ${v.change_kind}, ${formatDate(v.created_at)}`}
                    onClick={runCompare}
                  >
                    <span className="wb-shandle-pop__vseq">v{v.seq}</span>
                    <span className="wb-shandle-pop__vkind">{v.change_kind}</span>
                    <span className="wb-shandle-pop__vtime">
                      {formatDate(v.created_at)}
                    </span>
                    <ArrowRight size={12} strokeWidth={1.5} aria-hidden="true" />
                  </button>
                </li>
              ))}
          </ul>
        ) : null}
      </div>

      {link != null ? (
        <div
          className="wb-shandle-pop__group"
          role="none"
          data-testid={`section-link-${link.id}`}
          data-stale={stale ? "true" : "false"}
        >
          {stale ? (
            <div
              className="wb-shandle-pop__stale"
              data-testid={`stale-link-${link.id}`}
            >
              <AlertTriangle size={13} strokeWidth={1.5} aria-hidden="true" />
              Refers to {roleTitle(link.other_role as Kind)} v{link.other_pin_seq}
              {link.other_head_seq > link.other_pin_seq
                ? ` (now v${link.other_head_seq})`
                : ""}
            </div>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="wb-shandle-pop__item"
            data-testid={`section-link-chip-${link.id}`}
            onClick={() => {
              onCounterpart(link.other_role);
              onClose();
            }}
          >
            <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />↔{" "}
            {roleTitle(link.other_role as Kind)}:{" "}
            {link.other_heading || link.article_heading || "section"}
          </button>
          {stale ? (
            <button
              type="button"
              role="menuitem"
              className="wb-shandle-pop__item wb-shandle-pop__item--warn"
              data-testid={`repin-link-${link.id}`}
              onClick={() => {
                onRepin(link.id);
                onClose();
              }}
            >
              <RotateCcw size={14} strokeWidth={1.5} aria-hidden="true" />
              Re-pin to current head
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="wb-shandle-pop__group" role="none">
        <button
          type="button"
          role="menuitem"
          className="wb-shandle-pop__item"
          data-testid={`section-comment-${kind}-${index}`}
          onClick={() => {
            onComment();
            onClose();
          }}
        >
          <MessagesSquare size={14} strokeWidth={1.5} aria-hidden="true" />
          Comment on this section
        </button>
      </div>
    </div>,
    document.body,
  );
}
